import { Accordion, Box, Flex, Radio, Space, Text } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import { useState } from 'react';
import { useIsAfternoon } from 'src/common/useIsAfternoonHook';
import { TableHeader } from '../UI/common';
import EventsSvg from './Events.svg?react';
import FirstGraphSvg from './FirstGraph.svg?react';
import useExpenditures from './hooks/useExpenditures';
import useIncomes from './hooks/useIncomes';
import useLifeEvents from './hooks/useLifeEvents';
import NotesSvg from './Notes.svg?react';
import SecondGraphSvg from './SecondGraph.svg?react';
import PageLoader from 'src/UI/loader/PageLoader';
import { formatCurrency, Frequency } from './utils';
import { IncomePanel } from './form/IncomePanel';
import { ExpenditurePanel } from './form/ExpenditurePanel';
import { LifeEventPanel } from './form/LifeEventPanel';

export default function CashflowContent() {
  const {
    isLoading: loadingIncomes,
    incomesData,
    upsertIncomes,
    deleteIncomes,
    frequency: incomesFrequency,
    setFrequency: setIncomesFrequency,
    totalPreTax: incomesTotalPreTax,
    totalPostTax: incomesTotalPostTax,
  } = useIncomes();
  const {
    expendituresData,
    upsertExpenditures,
    deleteExpenditures,
    frequency: expendituresFrequency,
    setFrequency: setExpendituresFrequency,
    total: expendituresTotal,
  } = useExpenditures();
  const { lifeEventsData, upsertLifeEvents, deleteLifeEvents } = useLifeEvents();
  const { isCrawford } = useIsAfternoon();

  const [activePanel, setActivePanel] = useState<string | null>(null);

  const lifeEventsTotal = lifeEventsData
    ? lifeEventsData.reduce((total, event) => total + (event.cost || 0), 0)
    : 0;

  if (loadingIncomes) {
    return <PageLoader absolute={false} />;
  }

  return (
    <Box>
      <TableHeader
        columns={[
          { label: 'Type', align: 'left' },
          { label: 'Total', align: 'right' },
        ]}
        pr="56px"
      />
      <Accordion
        defaultValue={null}
        value={activePanel}
        onChange={val => setActivePanel(val)}
        variant="cells"
        chevron={<IconChevronDown size={20} stroke={1.5} />}
      >
        <Accordion.Item value="incomes" role="region">
          <Accordion.Control>
            <AccordionHeader
              title="Income"
              count={incomesData?.length || 0}
              amount={`${formatCurrency(incomesTotalPreTax)} (${formatCurrency(
                incomesTotalPostTax,
              )} after tax)`}
              signPrefix="+"
              frequency={incomesFrequency}
              setFrequency={frequency => setIncomesFrequency(frequency as Frequency)}
            />
          </Accordion.Control>
          <Accordion.Panel>
            <IncomePanel
              items={incomesData || []}
              onSave={upsertIncomes}
              onDelete={deleteIncomes}
              frequency={incomesFrequency}
            />
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="expenditures" role="region">
          <Accordion.Control>
            <AccordionHeader
              title="Expenditure"
              count={expendituresData?.length || 0}
              amount={formatCurrency(expendituresTotal)}
              signPrefix="-"
              frequency={expendituresFrequency}
              setFrequency={frequency => setExpendituresFrequency(frequency as Frequency)}
            />
          </Accordion.Control>
          <Accordion.Panel>
            <ExpenditurePanel
              items={expendituresData || []}
              onSave={upsertExpenditures}
              onDelete={deleteExpenditures}
              frequency={expendituresFrequency}
            />
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="lifeEvents" role="region">
          <Accordion.Control>
            <AccordionHeader
              title="Future life events"
              count={lifeEventsData?.length || 0}
              amount={formatCurrency(lifeEventsTotal)}
              amountLabel="Total"
              signPrefix="-"
            />
          </Accordion.Control>
          <Accordion.Panel>
            <LifeEventPanel
              items={lifeEventsData || []}
              onSave={upsertLifeEvents}
              onDelete={deleteLifeEvents}
            />
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
      {isCrawford && (
        <Box pr={'md'}>
          <Text size={'xl'} mt={'xxxxl'}>
            Key events
          </Text>
          <Text size={'sm'}>Last updated: 4 minutes ago</Text>
          <Space my={'xxl'} />
          <EventsSvg />
          <Space my={'xxl'} />
          <FirstGraphSvg />
          <Space my={'xxl'} />
          <SecondGraphSvg />
          <Space my={'xxl'} />
          <NotesSvg />
        </Box>
      )}
    </Box>
  );
}

type AccordionHeaderProps = {
  title: string;
  count: number;
  amount: string;
  amountLabel?: string;
  signPrefix?: string;
} & (
  | {
      frequency: Frequency;
      setFrequency: (frequency: string) => void;
    }
  | {
      frequency?: undefined;
      setFrequency?: undefined;
    }
);

const AccordionHeader = ({
  title,
  count,
  amount,
  amountLabel,
  signPrefix = '',
  frequency,
  setFrequency,
}: AccordionHeaderProps) => {
  const testId = title.toLowerCase().replace(/\s+/g, '-');

  return (
    <Box style={{ display: 'flex', justifyContent: 'space-between' }}>
      <Box>
        <Text size="sm" data-testid={`${testId}-count`}>
          {count} {count === 1 ? 'source' : 'sources'}
        </Text>
        <Text size="lg" fw={700}>
          {title}
        </Text>
      </Box>
      <Flex direction="column" align="flex-end">
        {frequency && (
          <Radio.Group
            value={frequency}
            onChange={setFrequency}
            onClick={event => event.stopPropagation()}
          >
            <Flex gap="xs">
              <Radio value="monthly" label="Monthly" />
              <Radio value="annually" label="Annually" />
            </Flex>
          </Radio.Group>
        )}
        <Box style={{ textAlign: 'right' }}>
          {amountLabel && <Text size="sm">{amountLabel}</Text>}
          <Text size="lg" fw={700} data-testid={`${testId}-amount`}>
            {signPrefix}
            {amount}
          </Text>
        </Box>
      </Flex>
    </Box>
  );
};
